/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { ACTIVITY_STATUS } from 'root/activities/hub/data/constants';
import {
  getCloseReadingProgressRatio,
  getSkillGamesProgressRatio,
  getVocabProgressRatio,
  getEreaderProgressRatio,
  getAreAllQuestActivitiesComplete,
} from 'a/hub/selectors/studentProgressSelectors';
import { getCurrentWorkshopQuest } from 'a/hub/selectors/workshopSelectors';
import svgMap from '../svgs/Common/svgMap';

const DiamondIcon = styled.img`
    position: absolute;
    width: 92px;
    height: 92px;
    object-fit: contain;
`;

const fillAnimation = keyframes`
  from {
    clip-path: inset(100% 0% 0% 0%);
  }
`;

const DiamondIconProgress = styled.img`
  animation: ${fillAnimation} 1s linear;
  clip-path: inset(${({ percent }) => `${100 - percent}`}% 0% 0% 0%);
  position: absolute;
  width: 92px;
  height: 92px;
  overflow: hidden;
  object-fit: contain;
  pointer-events: none;
`;

const Portal = styled.div`
  position: absolute; 
  top: 0;
  left: 0;
`;

const Book = styled.div`
  position: absolute;
  top: 55;
  left: 55;
`;

const Radio = styled.div`
  position: absolute;
  top: 55;
  left: -55;
`;

const Battery = styled.div`
  position: absolute;
  top: 110;
  left: 0;
`;

const DiamondCheck = styled.img`
  position: absolute;
  left: 35;
  top: 75;
  width: 23px;
  height: 23px;
  pointer-events: none;
`;

const PROGRESS_TYPE = {
  BOOK: 'Book',
  RADIO: 'Radio',
  PORTAL: 'Portal',
  BATTERY: 'Battery',
};

const TopContainer = styled.figure`
    margin: 0;
    position:absolute;
    top: ${({ top }) => top || 180};
    left: ${({ left }) => left || 300};
`;

const DIAMOND_CONTAINERS = {
  [PROGRESS_TYPE.BATTERY]: Battery,
  [PROGRESS_TYPE.BOOK]: Book,
  [PROGRESS_TYPE.PORTAL]: Portal,
  [PROGRESS_TYPE.RADIO]: Radio,
};

const ICON_LABELS = {
  [PROGRESS_TYPE.BATTERY]: 'Glyph-powered Battery',
  [PROGRESS_TYPE.BOOK]: 'Companion Reader',
  [PROGRESS_TYPE.PORTAL]: 'Trans-locational portal',
  [PROGRESS_TYPE.RADIO]: 'Radio Transmitter',
};

export const DiamondProgress = (props) => {
  const { battery: { status: batteryStatus } } = useSelector(getCurrentWorkshopQuest);

  const reduxBatteryProgress = useSelector(getSkillGamesProgressRatio);
  const reduxPortalProgress = useSelector(getCloseReadingProgressRatio);
  const reduxBookProgress = useSelector(getEreaderProgressRatio);
  const reduxRadioProgress = useSelector(getVocabProgressRatio);
  const reduxAreAllComplete = useSelector(getAreAllQuestActivitiesComplete);

  const batteryProgress = props.batteryProgress || reduxBatteryProgress;
  const portalProgress = props.portalProgress || reduxPortalProgress;
  const bookProgress = props.bookProgress || reduxBookProgress;
  const radioProgress = props.radioProgress || reduxRadioProgress;
  const areAllComplete = props.areAllComplete || reduxAreAllComplete;

  return (
    <TopContainer {...props} role="group" aria-label="Image with 4 activities">
      {renderDiamondIcon(PROGRESS_TYPE.PORTAL, portalProgress, areAllComplete)}
      {renderDiamondIcon(PROGRESS_TYPE.RADIO, radioProgress, areAllComplete)}
      {renderDiamondIcon(PROGRESS_TYPE.BOOK, bookProgress, areAllComplete)}
      {renderDiamondIcon(PROGRESS_TYPE.BATTERY, batteryStatus === ACTIVITY_STATUS.FINISHED ? 1
        : batteryProgress, areAllComplete)}
    </TopContainer>
  );
};

DiamondProgress.propTypes = {
  portalProgress: PropTypes.number,
  batteryProgress: PropTypes.number,
  bookProgress: PropTypes.number,
  radioProgress: PropTypes.number,
  areAllComplete: PropTypes.bool,
};

DiamondProgress.defaultProps = {
  portalProgress: 0,
  batteryProgress: 0,
  bookProgress: 0,
  radioProgress: 0,
  areAllComplete: false,
};

const getLabel = (progressType, completeRatio) => {
  const progress = Math.ceil(completeRatio * 100);
  const progressLabel = progress >= 100 ? 'Completed' : `Progress is ${progress}%`;
  const status = progress > 0 ? progressLabel : 'Inactive';

  return `${ICON_LABELS[progressType]}, ${status}`;
};

const getCompletionStatus = (completeRatio) => {
  const progress = Math.ceil(completeRatio * 100);
  const progressLabel = progress >= 100 ? 'completed' : `progress-${progress}`;
  const status = progress > 0 ? progressLabel : 'inactive';

  return status;
};

const renderDiamondIcon = (progressType, completeRatio, areAllComplete) => {
  const Container = DIAMOND_CONTAINERS[progressType];
  const isActivityComplete = completeRatio === 1;
  const iconState = completeRatio > 0 ? 'Active' : 'Inactive';
  const fillColor = areAllComplete ? 'Green' : '';
  return (
    <Container>
      <DiamondIcon
        src={svgMap[`Diamond${iconState}${progressType}`]}
        alt={getLabel(progressType, completeRatio)}
        data-testid={`login-modal-progress-${progressType}-${getCompletionStatus(completeRatio)}`}
      />
      <DiamondIconProgress
        src={svgMap[`DiamondProgress${fillColor}${progressType}`]}
        percent={completeRatio * 100}
        alt=""
      />
      {isActivityComplete && (
        <DiamondCheck src={areAllComplete ? svgMap.DiamondCheckGreen : svgMap.DiamondCheck} alt="" />
      )}
    </Container>
  );
};

export default DiamondProgress;
